import type { JSONContent } from '@tiptap/core';
import { ContentBlockType, MarkdownBlock, PopoverData } from '../../model';
import { BlockToTiptapConverter, ConverterContext } from '../blocks-to-tiptap.converter';
import {
  convertCustomIconNodesToText,
  convertEmojiNodesToText,
  convertMathNodesToText,
  convertPopoversToMarkers,
  processNodeForCustomIcons,
  processNodeForMath,
  processNodeForPopovers,
} from './processors';
import { getMarkdownManager } from './utils/markdown-manager.util';
import { escapeBackslashesInMath, escapeTildesInMath } from './utils/markdown.util';

type ConvertTiptapToMarkdownBlockFn = (
  nodes: JSONContent[],
  id: string,
  customCss?: string,
  context?: ConverterContext,
) => MarkdownBlock;

/**
 * Converts a MarkdownBlock to Tiptap JSON nodes.
 * Uses @tiptap/markdown MarkdownManager to parse the markdown string into Tiptap nodes.
 * Post-processes to convert LaTeX math syntax to inlineMath/blockMath nodes.
 * Post-processes to convert popover markers to popover marks.
 *
 * @param block - The MarkdownBlock to convert
 * @param context - Optional context to pass through (e.g., 'popover' to skip popover processing)
 * @returns An array of Tiptap JSON nodes (paragraph, heading, list, etc.)
 */
export const convertMarkdownBlockToTiptap = ((block, context?: ConverterContext): JSONContent[] => {
  const manager = getMarkdownManager();
  const doc = manager.parse(escapeBackslashesInMath(escapeTildesInMath(block.markdown)));
  const content = doc.content ?? [];

  // Post-process: popovers first (to capture complete markers), then math (to convert $...$ within text)

  return content.map((node) => {
    const processed = context?.popover ? node : processNodeForPopovers(node, block.popovers, context);
    return processNodeForCustomIcons(processNodeForMath(processed));
  });
}) satisfies BlockToTiptapConverter<MarkdownBlock>;

/**
 * Converts Tiptap JSON nodes back to a MarkdownBlock.
 * Uses @tiptap/markdown MarkdownManager to serialize nodes to markdown.
 * Pre-processes math nodes back to LaTeX syntax before serialization.
 * Pre-processes popover marks back to {{popover:id}}text{{/popover}} syntax.
 *
 * @param nodes - Array of Tiptap JSON nodes to convert
 * @param id - The block ID to use
 * @param customCss - Optional custom CSS classes
 * @returns A MarkdownBlock containing the serialized markdown
 */
export const convertTiptapToMarkdownBlock: ConvertTiptapToMarkdownBlockFn = (nodes, id, customCss?, context?) => {
  const manager = getMarkdownManager();

  const popoversMap: Record<string, PopoverData> = {};

  const processedNodes = nodes
    .map(convertMathNodesToText)
    .map(convertEmojiNodesToText)
    .map(convertCustomIconNodesToText)
    .map((node) => convertPopoversToMarkers(node, popoversMap, context));
  const doc: JSONContent = { type: 'doc', content: processedNodes };
  const markdown = manager.serialize(doc);

  return createMarkdownBlock(
    id,
    markdown.trim(),
    customCss,
    Object.keys(popoversMap).length > 0 ? popoversMap : undefined,
  );
};

/**
 * Creates a MarkdownBlock with the given properties.
 */
export function createMarkdownBlock(
  id: string,
  markdown: string,
  customCss?: string,
  popovers?: Record<string, PopoverData>,
): MarkdownBlock {
  return {
    id,
    type: ContentBlockType.Markdown,
    markdown,
    ...(customCss && { custom_css: customCss }),
    ...(popovers && { popovers }),
  };
}
